import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_87621aa9-388b-4a4a-b140-4ad09ad180a4/artifacts/4hsu6g13_Agilitie_logo_white-disc.svg";

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Services', path: '/services' },
  { name: 'Our People', path: '/people' },
  { name: 'Insights', path: '/insights' },
  { name: 'The Goldilocks Zone', path: '/the-goldilocks-zone' },
  { name: 'Media & Speaking', path: '/media-speaking' },
  { name: '#GivingBack', path: '/giving-back' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <header 
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 bg-white transition-all duration-300 ${
        isScrolled ? 'shadow-sm border-b border-[#e5e2dc]' : ''
      }`}
      style={{ overflow: 'visible', height: '70px' }}
    >
      <div 
        className="max-w-7xl mx-auto px-6 lg:px-8 h-full"
        style={{ overflow: 'visible' }}
      >
        <div 
          className="flex items-center justify-between h-full"
          style={{ overflow: 'visible' }}
        >
          {/* Logo - Responsive sizing */}
          <div style={{ overflow: 'visible', position: 'relative', zIndex: 100 }}>
            <Link 
              to="/" 
              data-testid="logo-link"
              className="block absolute"
              style={{ 
                top: '18px',
                left: 0
              }}
            >
              <img 
                src={LOGO_URL} 
                alt="Agilitie" 
                data-testid="logo-image"
                className="w-[120px] h-[120px] md:w-[150px] md:h-[150px] lg:w-[180px] lg:h-[180px]"
                style={{ 
                  objectFit: 'cover',
                  display: 'block',
                  borderRadius: '50%'
                }}
              />
            </Link>
            {/* Spacer to prevent nav overlap - responsive */}
            <div className="w-[120px] md:w-[150px] lg:w-[180px] h-[70px]" />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`font-dm-sans text-sm font-medium transition-colors duration-200 ${
                  isActive(link.path) 
                    ? 'text-[#d4782c]' 
                    : 'text-[#4a4744] hover:text-[#1a1a1a]'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="nav-contact-btn"
              className="btn-nav"
            >
              Contact
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-[#1a1a1a]"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation - right aligned */}
      {isMobileMenuOpen && (
        <nav 
          className="lg:hidden bg-white border-t border-[#e5e2dc] absolute top-full left-0 right-0 shadow-lg z-40"
          data-testid="mobile-nav"
        >
          <div className="px-6 py-4 space-y-4 text-right">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`block font-dm-sans text-base font-medium py-2 ${
                  isActive(link.path) 
                    ? 'text-[#d4782c]' 
                    : 'text-[#4a4744]'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-nav-contact-btn"
              className="block btn-nav text-center mt-4"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Contact
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
};
